/* Maps the errors the email/password flows can throw — Firebase Auth error
 * codes (FirebaseError.code, e.g. "auth/invalid-credential") and our own
 * SignInError — to short, sentence-case, next-step messages (per the DS voice
 * rules). Centralized so Login / Signup / Forgot-password all speak the same
 * language. The defaults are deliberately generic to avoid leaking which
 * emails exist (account enumeration). */

import { SignInError } from "./identitySession";
import { APP_BASENAME, COMING_SOON_PATH } from "./paths";

/** On an EMAIL_NOT_ALLOWLISTED SignInError, full-page-redirect to /coming-soon and return true (works outside the router too, e.g. the session bootstrap). */
export function redirectIfEmailNotAllowed(err: unknown): boolean {
  if (err instanceof SignInError && err.code === "email_not_allowed") {
    window.location.assign(`${APP_BASENAME}${COMING_SOON_PATH}`);
    return true;
  }

  return false;
}

/** Duck-typed Firebase error code; FirebaseError carries a string `code`. */
function firebaseCode(err: unknown): string | null {
  if (err && typeof err === "object" && "code" in err) {
    const code = (err as { code: unknown }).code;
    return typeof code === "string" ? code : null;
  }

  return null;
}

const LOGIN_FALLBACK =
  "Couldn't sign you in. Check your details and try again.";

/** Message for a failed sign-in (email/password or social). The verified gate
 *  lives in session.tsx (the `emailVerified` field), not here — a generic
 *  identity SignInError falls through to the fallback. */
export function loginErrorMessage(err: unknown): string {
  switch (firebaseCode(err)) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
      // Kept identical so the form can't be used to probe which emails exist.
      return "Incorrect email or password.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/user-disabled":
      return "This account has been disabled. Contact your administrator.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/operation-not-allowed":
      // Email/password provider not enabled on the Firebase project.
      return "Email and password sign-in isn't available right now. Try Google.";
    default:
      return LOGIN_FALLBACK;
  }
}

/** Message for a failed sign-up. */
export function signupErrorMessage(err: unknown): string {
  switch (firebaseCode(err)) {
    case "captcha/verification-failed":
      return "Couldn't verify you're human. Refresh the page and try again.";
    case "captcha/token-missing":
      return "Please complete the verification challenge, then try again.";
    case "auth/email-already-in-use":
      // The email may already exist via a social provider with no password —
      // hence "sign in with your existing method", not just "sign in".
      return "An account with this email already exists. Sign in with your existing method instead.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/weak-password":
      return "Choose a stronger password — at least 8 characters.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    case "auth/operation-not-allowed":
      return "Email and password sign-up isn't available right now. Try Google.";
    default:
      return "Couldn't create your account. Try again.";
  }
}

/** Message for a failed password-reset request. With Firebase email-enumeration
 *  protection on, sending never reveals whether the address exists, so the
 *  screen shows a generic "if an account exists…" confirmation regardless;
 *  this covers only the hard failures. */
export function passwordResetErrorMessage(err: unknown): string {
  switch (firebaseCode(err)) {
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a few minutes and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return "Couldn't send the reset email. Try again.";
  }
}

/** Message for a failed verification-email resend. */
export function resendErrorMessage(err: unknown): string {
  // Server rate limit is 1/min → HTTP 429 surfaces as a generic Error.
  if (err instanceof Error && /429/.test(err.message)) {
    return "Please wait a moment before requesting another email.";
  }

  return "Couldn't resend the email. Try again in a moment.";
}
